/**
 * PTEL Tool - PasswordResetForm
 * Formulario de recuperación de contraseña
 *
 * @module components/auth/PasswordResetForm
 * @version 1.2.0
 * @changes
 *   1.2.0 — Añade prop hideCard?: boolean para renderizarse sin card
 *            propio cuando AuthModal lo integra en su card unificado.
 *            Corrige error TS2322 del build (property 'hideCard' does not exist).
 */

import type { FormEvent, CSSProperties } from 'react';
import { useState, useCallback } from 'react';
import { Mail, AlertCircle, Loader2, CheckCircle2, ArrowLeft, X } from 'lucide-react';

import type { PasswordResetFormProps } from './auth.types';
import { VALIDATION_RULES } from './auth.types';
import { getAuthTokens, getButtonStyles, getCardStyles, getAlertStyles } from './auth.tokens';
import { useIsMounted } from '@/hooks/useIsMounted';

function validateEmail(email: string): string | undefined {
  if (!email.trim()) return VALIDATION_RULES.email.required;
  if (!VALIDATION_RULES.email.pattern.test(email)) return VALIDATION_RULES.email.invalid;
  return undefined;
}

export function PasswordResetForm({
  theme,
  onSubmit,
  onNavigateToLogin,
  isLoading: externalLoading = false,
  externalError,
  onClearError,
  onClose,
  hideCard = false,
}: PasswordResetFormProps & { hideCard?: boolean }) {
  const t = getAuthTokens(theme);
  const buttonStyles = getButtonStyles(theme);
  const secondaryButtonStyles = getButtonStyles(theme, 'secondary');
  const cardStyles = getCardStyles(theme);
  const alertStyles = getAlertStyles(theme, 'error');

  const isMounted = useIsMounted();

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const isLoading = externalLoading || isSubmitting;

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) setError(undefined);
    if (externalError && onClearError) onClearError();
  }, [error, externalError, onClearError]);

  const handleBlur = useCallback(() => {
    setTouched(true);
    setFocused(false);
    setError(validateEmail(email));
  }, [email]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    setError(emailError);
    setTouched(true);
    if (emailError) return;
    setIsSubmitting(true);
    try {
      await onSubmit(email);
      if (isMounted()) setIsSuccess(true);
    } finally {
      if (isMounted()) setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try { await onSubmit(email); }
    finally { if (isMounted()) setIsResending(false); }
  };

  const hasError = touched && error;

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '10px 12px 10px 40px',
    background: t.bgInput,
    border: `1px solid ${hasError ? t.borderError : focused ? t.borderFocus : t.border}`,
    borderRadius: '8px',
    color: t.text,
    fontSize: '14px',
    fontFamily: "'JetBrains Mono', monospace",
    outline: 'none',
    boxShadow: focused ? `0 0 0 3px ${hasError ? t.errorBg : t.focusRing}` : 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };
  const iconStyle: CSSProperties = {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)', color: t.textMuted, pointerEvents: 'none',
  };
  const labelStyle: CSSProperties = {
    display: 'block', marginBottom: '6px', fontSize: '14px',
    fontWeight: 500, color: t.textSecondary, fontFamily: "'Space Grotesk', sans-serif",
  };
  const errStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '4px',
    marginTop: '4px', fontSize: '12px', color: t.error,
  };

  const closeBtnInline: CSSProperties = {
    position: 'absolute', top: '12px', right: '12px', zIndex: 10,
    width: '32px', height: '32px', background: 'transparent',
    border: `1px solid ${t.border}`, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: t.textSecondary, cursor: 'pointer', padding: 0,
  };

  // ── Pantalla de éxito ────────────────────────────────────────────────────
  const successContent = (
    <>
      <div style={{
        width: '64px', height: '64px', background: t.successBg, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
      }}>
        <CheckCircle2 size={32} style={{ color: t.success }} />
      </div>
      <h2 style={cardStyles.title}>Correo enviado</h2>
      <p style={{ fontSize: '14px', color: t.textSecondary, textAlign: 'center', marginBottom: '8px' }}>
        Si existe una cuenta asociada a este correo, recibirás instrucciones para restablecer tu contraseña en
      </p>
      <p style={{ fontSize: '14px', color: t.text, fontWeight: 600, textAlign: 'center', marginBottom: '16px', fontFamily: "'JetBrains Mono', monospace" }}>
        {email}
      </p>
      <p style={{ fontSize: '13px', color: t.textMuted, textAlign: 'center', marginBottom: '24px' }}>
        Si no recibes el correo en unos minutos, revisa la carpeta de spam.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <button type="button" onClick={handleResend} disabled={isResending}
          style={{ ...secondaryButtonStyles.base, ...(isResending ? secondaryButtonStyles.disabled : {}) }}>
          {isResending
            ? <><Loader2 size={16} className="animate-spin" /><span>Reenviando...</span></>
            : <span>Reenviar correo</span>}
        </button>
        {onNavigateToLogin && (
          <button type="button" onClick={onNavigateToLogin} style={buttonStyles.base}>
            Volver a iniciar sesión
          </button>
        )}
      </div>
    </>
  );

  // ── Formulario ───────────────────────────────────────────────────────────
  const formContent = (
    <>
      {onNavigateToLogin && (
        <button type="button" onClick={onNavigateToLogin} disabled={isLoading}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            background: 'none', border: 'none', padding: 0, marginBottom: '16px',
            fontSize: '14px', color: t.textSecondary, cursor: 'pointer',
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
          <ArrowLeft size={16} /><span>Volver</span>
        </button>
      )}
      <div style={{
        width: '48px', height: '48px', background: t.successBg, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
      }}>
        <Mail size={24} style={{ color: t.success }} />
      </div>
      <h2 style={cardStyles.title}>Recuperar contraseña</h2>
      <p style={cardStyles.subtitle}>
        Introduce tu correo electrónico y te enviaremos instrucciones para restablecer tu contraseña de acceso a PTEL Tool.
      </p>
      {externalError && (
        <div style={alertStyles.container}>
          <AlertCircle size={18} style={alertStyles.icon} />
          <span style={alertStyles.text}>{externalError}</span>
        </div>
      )}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div>
          <label htmlFor="reset-email" style={labelStyle}>Correo electrónico</label>
          <div style={{ position: 'relative' }}>
            <Mail size={18} style={iconStyle} aria-hidden="true" />
            <input id="reset-email" type="email" value={email}
              onChange={handleEmailChange} onFocus={() => setFocused(true)} onBlur={handleBlur}
              placeholder="tu@email.com" disabled={isLoading} autoFocus autoComplete="email"
              style={inputStyle}
            />
          </div>
          {hasError && <div style={errStyle}><AlertCircle size={12} /><span>{error}</span></div>}
        </div>
        <button type="submit" disabled={isLoading}
          style={{ ...buttonStyles.base, ...(isLoading ? buttonStyles.disabled : {}) }}>
          {isLoading
            ? <><Loader2 size={18} className="animate-spin" /><span>Enviando...</span></>
            : <span>Enviar instrucciones</span>}
        </button>
      </form>
      {onNavigateToLogin && (
        <p style={{ ...cardStyles.footer, marginTop: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
          <span>¿Recordaste tu contraseña?</span>
          <button type="button" onClick={onNavigateToLogin} disabled={isLoading} style={cardStyles.link}>
            Iniciar sesión
          </button>
        </p>
      )}
    </>
  );

  const content = isSuccess ? successContent : formContent;

  // ── Modo integrado (sin card propio) ─────────────────────────────────────
  if (hideCard) {
    return (
      <>
        {content}
        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .animate-spin { animation: spin 1s linear infinite; }
        `}</style>
      </>
    );
  }

  // ── Modo standalone (con card propio) ────────────────────────────────────
  return (
    <div style={{ width: '100%', maxWidth: '440px', margin: '0 auto' }}>
      <div style={{ ...cardStyles.container, position: 'relative' }}>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Cerrar" style={closeBtnInline}>
            <X size={16} />
          </button>
        )}
        {content}
      </div>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

export default PasswordResetForm;
